import debounce from 'lodash/debounce'
import { action, makeObservable, observable } from 'mobx'

import SortOrder from '~/client/src/shared/enums/SortOrder'

import {
  WorkflowGroupingKey,
  WorkflowSortingKey,
} from '../WorkflowList/WorkflowList.types'

interface IInitialDefaults {
  initialSortKey?: WorkflowSortingKey | null
  initialSortOrder?: SortOrder
  initialGroupByKey?: WorkflowGroupingKey | null
}

export default class WorkflowDirectoryStore {
  @observable public groupingKey: WorkflowGroupingKey | null = null
  @observable public sortingKey: WorkflowSortingKey | null = null
  @observable public sortOrder: SortOrder = SortOrder.DESC

  @observable public searchKey: string = ''
  @observable public debouncedSearchKey: string = ''
  @observable public isMyWorkflowsActive: boolean = false
  @observable public isMyCompanyActive: boolean = false

  public constructor(initialDefaults?: IInitialDefaults) {
    if (initialDefaults?.initialSortKey !== undefined) {
      this.sortingKey = initialDefaults.initialSortKey
    }
    if (initialDefaults?.initialSortOrder !== undefined) {
      this.sortOrder = initialDefaults.initialSortOrder
    }
    if (initialDefaults?.initialGroupByKey !== undefined) {
      this.groupingKey = initialDefaults.initialGroupByKey
    }

    makeObservable(this)
  }

  @action.bound
  public setGroupingKey(key: WorkflowGroupingKey | null) {
    this.groupingKey = key
  }

  // One update for key and direction, so the list re-sorts once.
  @action.bound
  public setSort(key: WorkflowSortingKey | null, order?: SortOrder) {
    this.sortingKey = key
    if (order !== undefined) this.sortOrder = order
  }

  @action.bound
  public setSearch(search: string) {
    this.searchKey = search
    // Clearing applies at once; only typing waits for the debounce.
    if (!search) {
      this.updateDebouncedSearch.cancel()
      this.debouncedSearchKey = ''
      return
    }
    this.updateDebouncedSearch(search)
  }

  private updateDebouncedSearch = debounce(
    action((search: string) => {
      this.debouncedSearchKey = search
    }),
    300,
  )

  public dispose() {
    this.updateDebouncedSearch.cancel()
  }

  @action.bound
  public toggleMyWorkflows() {
    this.isMyWorkflowsActive = !this.isMyWorkflowsActive
  }

  @action.bound
  public toggleMyCompany() {
    this.isMyCompanyActive = !this.isMyCompanyActive
  }
}
